import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import AppBar from "../../components/AppBar/AppBar";
import { fetchJobDetailApi } from "../../services/job.services";
import DictionarySearchPanel from "../components/DictionarySearchPanel";
import JobAudioPreview from "../components/JobAudioPreview";
import SegmentListItem from "../components/SegmentListItem";
import TokenFieldRow from "../components/TokenFieldRow";
import WordChip from "../components/WordChip";
import {
  useAddMappingsMutation,
  useAddTempEntryMutation,
  useJobProgress,
  useJobSegments,
  useJobWords,
  useRemoveMappingMutation,
  useSyncJobWordsMutation,
  useUpdateSegmentPhoneticsMutation,
} from "../hooks/useVocabQueries";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "unmapped", label: "Unmapped" },
  { value: "mapped", label: "Mapped" },
  { value: "partial", label: "Partial" },
  { value: "skipped", label: "Skipped" },
];

export default function JobMappingPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);
  const [selectedWordId, setSelectedWordId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingEntryIds, setPendingEntryIds] = useState([]);
  const [showTempForm, setShowTempForm] = useState(false);
  const [tempLex, setTempLex] = useState("");
  const [tempPhono, setTempPhono] = useState("");
  const [tempGloss, setTempGloss] = useState("");
  const [caphiDrafts, setCaphiDrafts] = useState({});
  const [codaDrafts, setCodaDrafts] = useState({});
  const [phoneticsDirty, setPhoneticsDirty] = useState(false);
  const audioPreviewRef = useRef(null);
  const tempGlossRef = useRef(null);

  useEffect(() => {
    fetchJobDetailApi({ jobId }).then(setJob).catch(() => {});
  }, [jobId]);

  const segmentsQuery = useJobSegments({
    queryConfig: {
      queryKey: [jobId, statusFilter],
      apiParams: { jobId, status: statusFilter || undefined },
    },
  });

  const syncMutation = useSyncJobWordsMutation({
    mutationConfig: {
      onSuccess: () => toast.success("Words synced from annotations"),
      onError: () => toast.error("Failed to sync words"),
    },
  });

  useEffect(() => {
    if (jobId) {
      syncMutation.mutate({ jobId, suggest: false });
    }
    // Sync once when opening the mapping workspace for this job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const segments = segmentsQuery.data || [];

  useEffect(() => {
    if (!selectedSegmentId && segments.length > 0) {
      setSelectedSegmentId(segments[0].id);
    }
  }, [segments, selectedSegmentId]);

  const wordsQuery = useJobWords({
    queryConfig: {
      queryKey: [jobId, selectedSegmentId],
      apiParams: {
        jobId,
        shape_id: selectedSegmentId,
        page_size: 200,
        suggest: true,
      },
      enabled: Boolean(selectedSegmentId),
    },
  });

  const progressQuery = useJobProgress({
    queryConfig: {
      queryKey: [jobId],
      apiParams: { jobId },
    },
  });

  const words = wordsQuery.data?.results || wordsQuery.data || [];
  const selectedWord = words.find((w) => w.id === selectedWordId) || words[0];
  const selectedSegment = segments.find((s) => s.id === selectedSegmentId);
  const wordsSignature = words
    .map(
      (word) =>
        `${word.id}:${word.caphi_token}:${word.coda_token}:${word.suggested_caphi}:${word.suggested_coda}`
    )
    .join("|");

  useEffect(() => {
    if (words.length && !selectedWordId) {
      setSelectedWordId(words[0].id);
    }
  }, [words, selectedWordId]);

  useEffect(() => {
    const nextCaphi = {};
    const nextCoda = {};
    words.forEach((word) => {
      nextCaphi[word.id] = word.caphi_token || word.suggested_caphi || "";
      nextCoda[word.id] = word.coda_token || word.suggested_coda || "";
    });
    setCaphiDrafts(nextCaphi);
    setCodaDrafts(nextCoda);
    const hasSuggestions = words.some(
      (word) =>
        (!word.caphi_token && word.suggested_caphi) ||
        (!word.coda_token && word.suggested_coda)
    );
    setPhoneticsDirty(hasSuggestions);
    // Reset drafts when the loaded word tokens/suggestions change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordsSignature]);

  useEffect(() => {
    if (selectedWord) {
      setSearchQuery(selectedWord.surface_normalized || selectedWord.caphi_normalized || "");
      setPendingEntryIds([]);
      setShowTempForm(false);
      setTempLex(selectedWord.surface || "");
      setTempPhono(selectedWord.caphi_token || "");
      setTempGloss("");
    }
  }, [selectedWord?.id]);

  const addMappingsMutation = useAddMappingsMutation({
    mutationConfig: {
      onSuccess: () => {
        toast.success("Mapping saved");
        setPendingEntryIds([]);
      },
      onError: () => toast.error("Failed to save mapping"),
    },
  });

  const removeMappingMutation = useRemoveMappingMutation({
    mutationConfig: {
      onSuccess: () => toast.success("Mapping removed"),
    },
  });

  const updatePhoneticsMutation = useUpdateSegmentPhoneticsMutation({
    mutationConfig: {
      onSuccess: () => {
        toast.success("CAPHI/CODA saved");
        setPhoneticsDirty(false);
      },
      onError: () => toast.error("Failed to save CAPHI/CODA"),
    },
  });

  const addTempEntryMutation = useAddTempEntryMutation({
    mutationConfig: {
      onSuccess: () => {
        toast.success("Temp entry added");
        setShowTempForm(false);
        setTempGloss("");
      },
      onError: () => toast.error("Failed to add temp entry"),
    },
  });

  const mappedEntryIds = useMemo(
    () => selectedWord?.mappings?.map((m) => m.dictionary_entry.id) || [],
    [selectedWord]
  );

  const handleSelectEntry = (entry) => {
    setPendingEntryIds((prev) =>
      prev.includes(entry.id) ? prev.filter((id) => id !== entry.id) : [...prev, entry.id]
    );
  };

  const handleAddMappings = () => {
    if (!selectedWord || pendingEntryIds.length === 0) return;
    addMappingsMutation.mutate({
      wordId: selectedWord.id,
      entryIds: pendingEntryIds,
    });
  };

  const handleAcceptTopSuggestion = useCallback(() => {
    const top = selectedWord?.suggestions?.[0];
    const entry = top?.entry;
    if (!entry?.id) return;
    addMappingsMutation.mutate({
      wordId: selectedWord.id,
      entryIds: [entry.id],
      confidence: top.confidence || "auto_exact",
    });
  }, [selectedWord, addMappingsMutation]);

  const handleAddTempEntry = useCallback(() => {
    if (!selectedWord) return;
    addTempEntryMutation.mutate({
      wordId: selectedWord.id,
      lex: tempLex,
      phono: tempPhono,
      englishGloss: tempGloss,
    });
  }, [selectedWord, addTempEntryMutation, tempLex, tempPhono, tempGloss]);

  const openTempForm = useCallback(() => {
    if (!selectedWord) return;
    setTempLex(selectedWord.surface || "");
    setTempPhono(selectedWord.caphi_token || "");
    setShowTempForm(true);
    window.setTimeout(() => tempGlossRef.current?.focus(), 0);
  }, [selectedWord]);

  const handleCaphiChange = (wordId, value) => {
    setCaphiDrafts((prev) => ({ ...prev, [wordId]: value }));
    setPhoneticsDirty(true);
  };

  const handleCodaChange = (wordId, value) => {
    setCodaDrafts((prev) => ({ ...prev, [wordId]: value }));
    setPhoneticsDirty(true);
  };

  const handleSavePhonetics = () => {
    if (!selectedSegment || !words.length) return;
    updatePhoneticsMutation.mutate({
      segmentId: selectedSegment.id,
      tokens: words.map((word) => ({
        id: word.id,
        caphi_token: caphiDrafts[word.id] || "",
        coda_token: codaDrafts[word.id] || "",
      })),
    });
  };

  const isCaphiSuggestion = (word, value) =>
    !word.caphi_token && Boolean(word.suggested_caphi) && value === word.suggested_caphi;

  const isCodaSuggestion = (word, value) =>
    !word.coda_token && Boolean(word.suggested_coda) && value === word.suggested_coda;

  const caphiMismatch =
    selectedSegment &&
    selectedSegment.caphi_token_count > 0 &&
    selectedSegment.transcript_word_count > 0 &&
    selectedSegment.caphi_token_count !== selectedSegment.transcript_word_count;
  const codaMismatch =
    selectedSegment &&
    selectedSegment.coda_token_count > 0 &&
    selectedSegment.transcript_word_count > 0 &&
    selectedSegment.coda_token_count !== selectedSegment.transcript_word_count;

  const handleSelectSegment = useCallback((segmentId) => {
    setSelectedSegmentId(segmentId);
    setSelectedWordId(null);
    audioPreviewRef.current?.seekToSegment(segmentId);
  }, []);

  const moveWord = useCallback(
    (direction) => {
      if (!words.length || !selectedWord) return;
      const index = words.findIndex((w) => w.id === selectedWord.id);
      const nextIndex = index + direction;
      if (nextIndex >= 0 && nextIndex < words.length) {
        setSelectedWordId(words[nextIndex].id);
      }
    },
    [words, selectedWord]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA") return;
      if (event.key === " " || event.code === "Space") {
        event.preventDefault();
        audioPreviewRef.current?.playPause();
        return;
      }
      if (event.key === "j") moveWord(1);
      if (event.key === "k") moveWord(-1);
      if (event.key === "Enter") handleAcceptTopSuggestion();
      if (event.key === "s" && selectedWord) {
        event.preventDefault();
        openTempForm();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [moveWord, handleAcceptTopSuggestion, selectedWord, openTempForm]);

  const progress = progressQuery.data;

  return (
    // <div className="min-h-screen dark:bg-ramsalab-deep-navy">
    <>
    <AppBar>
      <header className="py-10">
        <div className="mx-auto flex justify-between items-center max-w-7xl px-4 sm:px-6 lg:px-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-white">
                Map vocabulary · Job #{jobId}
              </h1>
            </div>
          </div>
      </header>
    </AppBar>
    <main className=" -mt-32 mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setStatusFilter(filter.value)}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  statusFilter === filter.value
                    ? "bg-ramsalab-teal-blue text-white"
                    : "bg-gray-100 text-gray-700 dark:bg-ramsalab-navy dark:text-gray-300"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 text-sm text-white-600 dark:text-white-300">
            {progress && (
              <span>
                {progress.mapped}/{progress.total} mapped
                {progress.unmapped > 0 && ` · ${progress.unmapped} unmapped`}
              </span>
            )}
            {job?.task_id && (
              <Link
                to={`/annotate/${job.task_id}/${jobId}`}
                className="text-white hover:underline"
              >
                Open in annotator
              </Link>
            )}
            <button
              type="button"
              onClick={() => syncMutation.mutate({ jobId })}
              disabled={syncMutation.isLoading}
              className="rounded-md bg-gray-100 px-3 py-1 text-xs font-medium dark:bg-ramsalab-navy"
            >
              Re-sync
            </button>
          </div>
        </div>

        <JobAudioPreview
          key={jobId}
          ref={audioPreviewRef}
          jobId={jobId}
          segments={segments}
          selectedSegmentId={selectedSegmentId}
          onSelectSegment={handleSelectSegment}
        />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6 min-h-[70vh]">
          <section className="lg:col-span-3 rounded-xl border border-gray-200 dark:border-ramsalab-light-navy bg-white dark:bg-ramsalab-navy p-4 overflow-y-auto max-h-[58vh]">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Segments</h2>
            <div className="space-y-2">
              {segmentsQuery.isLoading && <p className="text-sm text-gray-500">Loading…</p>}
              {segments.map((segment) => (
                <SegmentListItem
                  key={segment.id}
                  segment={segment}
                  isSelected={segment.id === selectedSegmentId}
                  onClick={() => handleSelectSegment(segment.id)}
                />
              ))}
            </div>
          </section>

          <section className="lg:col-span-5 min-w-0 overflow-y-auto max-h-[58vh] rounded-xl border border-gray-200 dark:border-ramsalab-light-navy bg-white dark:bg-ramsalab-navy p-4">
            {selectedSegment ? (
              <>
                <div className="mb-4 space-y-2 text-sm">
                  <p className="text-gray-500">
                    {selectedSegment.start != null && `${selectedSegment.start.toFixed(2)}s – ${selectedSegment.end?.toFixed(2)}s`}
                  </p>
                  <div className="w-full h-px bg-gray-200 dark:bg-ramsalab-light-navy my-2"></div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 font-mono">Transcript:</p>
                  <p className="text-base text-gray-900 dark:text-white" dir="auto">
                    {selectedSegment.transcript}
                  </p>
                </div>
                <div className="w-full h-px bg-gray-200 dark:bg-ramsalab-light-navy my-2"></div>
                <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-600 dark:text-gray-300 font-mono">Words:</p>
                    <div className="h-3"></div>
                    <div className="flex flex-wrap gap-2">
                      {words.map((word) => (
                        <WordChip
                          key={word.id}
                          word={word}
                          isSelected={word.id === selectedWord?.id}
                          onClick={() => setSelectedWordId(word.id)}
                        />
                      ))}
                    </div>
                  </div>
                  {selectedWord && (
                    <div className="sm:w-1/2 sm:max-w-sm rounded-lg border border-gray-200 dark:border-ramsalab-light-navy p-3">
                      <p className="text-xs text-gray-500 mb-2">
                        Current mappings · j/k next/prev · Enter accept suggestion · s add temp · Space play/pause
                      </p>
                      {selectedWord.mappings?.length ? (
                        <ul className="space-y-2">
                          {selectedWord.mappings.map((mapping) => (
                            <li
                              key={mapping.id}
                              className="flex items-start justify-between gap-2 text-sm"
                            >
                              <div>
                                <p className="font-medium dark:text-white" dir="auto">
                                  {mapping.dictionary_entry.entry || mapping.dictionary_entry.lex}
                                  {mapping.dictionary_entry.is_temporary && (
                                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                                      Temp
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {mapping.dictionary_entry.english_gloss}
                                </p>
                                {mapping.dictionary_entry.group_id && (
                                  <p className="text-[11px] text-gray-400" dir="auto">
                                    {mapping.dictionary_entry.group_id} · lemma{" "}
                                    {mapping.dictionary_entry.lex}
                                  </p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  removeMappingMutation.mutate({
                                    wordId: selectedWord.id,
                                    entryId: mapping.dictionary_entry.id,
                                  })
                                }
                                className="text-xs text-red-600 hover:underline"
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500">No mappings yet.</p>
                      )}

                      {selectedWord.suggestions?.length > 0 && (
                        <div className="mt-3 border-t border-gray-100 dark:border-ramsalab-light-navy pt-3">
                          <p className="text-xs font-medium text-amber-700 mb-2">Suggested matches</p>
                          <div className="space-y-1">
                            {selectedWord.suggestions.slice(0, 10).map((suggestion) => {
                              const entry = suggestion.entry || suggestion;
                              const rankLabel =
                                suggestion.source === "prior"
                                  ? "Previously mapped"
                                  : suggestion.rank > 0
                                    ? `Rank ${suggestion.rank}`
                                    : "Exact";
                              return (
                                <button
                                  key={entry.id}
                                  type="button"
                                  onClick={() =>
                                    addMappingsMutation.mutate({
                                      wordId: selectedWord.id,
                                      entryIds: [entry.id],
                                      confidence: suggestion.confidence || "auto_exact",
                                    })
                                  }
                                  className="w-full min-w-0 whitespace-normal break-words text-left text-sm rounded px-2 py-1 hover:bg-amber-50 dark:hover:bg-ramsalab-light-navy"
                                >
                                  <span dir="auto">{entry.lex}</span>
                                  <span className="block break-words">
                                    {entry.english_gloss}
                                  </span>
                                  <span className="block text-xs text-gray-500">
                                    {rankLabel}
                                    {entry.pos ? ` · ${entry.pos}` : ""}
                                    {entry.group_id ? ` · ${entry.group_id}` : ""}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="mt-3 space-y-2">
                        {!showTempForm ? (
                          <button
                            type="button"
                            onClick={openTempForm}
                            className="rounded-md border px-3 py-1.5 text-xs font-medium"
                          >
                            Add entry
                          </button>
                        ) : (
                          <form
                            className="space-y-2 rounded-md border border-gray-200 p-2 dark:border-ramsalab-light-navy"
                            onSubmit={(event) => {
                              event.preventDefault();
                              handleAddTempEntry();
                            }}
                          >
                            <label className="block text-xs text-gray-500">
                              Lex
                              <input
                                type="text"
                                value={tempLex}
                                onChange={(e) => setTempLex(e.target.value)}
                                dir="auto"
                                className="mt-1 w-full rounded border px-2 py-1 text-sm dark:bg-ramsalab-deep-navy dark:text-white"
                              />
                            </label>
                            <label className="block text-xs text-gray-500">
                              CAPHI
                              <input
                                type="text"
                                value={tempPhono}
                                onChange={(e) => setTempPhono(e.target.value)}
                                className="mt-1 w-full rounded border px-2 py-1 font-mono text-sm dark:bg-ramsalab-deep-navy dark:text-white"
                              />
                            </label>
                            <label className="block text-xs text-gray-500">
                              English gloss / note
                              <input
                                ref={tempGlossRef}
                                type="text"
                                value={tempGloss}
                                onChange={(e) => setTempGloss(e.target.value)}
                                placeholder="Optional"
                                className="mt-1 w-full rounded border px-2 py-1 text-sm dark:bg-ramsalab-deep-navy dark:text-white"
                              />
                            </label>
                            <div className="flex gap-2">
                              <button
                                type="submit"
                                disabled={addTempEntryMutation.isLoading}
                                className="rounded-md bg-ramsalab-teal-blue px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                              >
                                Save mapping
                              </button>
                              <button
                                type="button"
                                onClick={() => setShowTempForm(false)}
                                className="rounded-md border px-3 py-1.5 text-xs font-medium"
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="w-full h-px bg-gray-200 dark:bg-ramsalab-light-navy my-2"></div>
                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300 font-mono mb-2">
                    CAPHI:
                  </p>
                  {caphiMismatch && (
                    <p className="mb-2 text-xs text-amber-700">
                      CAPHI token count ({selectedSegment.caphi_token_count}) does not
                      match word count ({selectedSegment.transcript_word_count}).
                    </p>
                  )}
                  <TokenFieldRow
                    words={words}
                    values={caphiDrafts}
                    onChange={handleCaphiChange}
                    selectedWordId={selectedWord?.id}
                    onSelect={setSelectedWordId}
                    isSuggestion={isCaphiSuggestion}
                    mono
                  />
                  {selectedSegment.caphi && (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 font-mono" dir="auto">
                      {selectedSegment.caphi}
                    </p>
                  )}
                </div>
                <div className="w-full h-px bg-gray-200 dark:bg-ramsalab-light-navy my-2"></div>
                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300 font-mono mb-2">
                    CODA:
                  </p>
                  {codaMismatch && (
                    <p className="mb-2 text-xs text-amber-700">
                      CODA token count ({selectedSegment.coda_token_count}) does not
                      match word count ({selectedSegment.transcript_word_count}).
                    </p>
                  )}
                  <TokenFieldRow
                    words={words}
                    values={codaDrafts}
                    onChange={handleCodaChange}
                    selectedWordId={selectedWord?.id}
                    onSelect={setSelectedWordId}
                    isSuggestion={isCodaSuggestion}
                    dir="auto"
                  />
                  {selectedSegment.coda && (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300" dir="auto">
                      {selectedSegment.coda}
                    </p>
                  )}
                </div>
                <div className="mb-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSavePhonetics}
                    disabled={!phoneticsDirty || updatePhoneticsMutation.isLoading || !words.length}
                    className="rounded-md bg-ramsalab-teal-blue px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                  >
                    Save CAPHI/CODA
                  </button>
                </div>
                <div className="w-full h-px bg-gray-200 dark:bg-ramsalab-light-navy my-2"></div>
                <p className="text-sm text-gray-600 dark:text-gray-300 font-mono">Translation:</p>
                {selectedSegment.en_translation && (
                  <p className="mb-4 text-sm text-gray-500">{selectedSegment.en_translation}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500">Select a segment to begin mapping.</p>
            )}
          </section>

          <section className="lg:col-span-4 flex flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-ramsalab-light-navy bg-white dark:bg-ramsalab-navy p-4 max-h-[58vh]">
            <h2 className="shrink-0 text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Dictionary
            </h2>
            <DictionarySearchPanel
              className="flex-1 min-h-0"
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onSelectEntry={handleSelectEntry}
              selectedEntryIds={[...mappedEntryIds, ...pendingEntryIds]}
              jobId={jobId}
            />
            {pendingEntryIds.length > 0 && (
              <button
                type="button"
                onClick={handleAddMappings}
                disabled={addMappingsMutation.isLoading}
                className="mt-3 shrink-0 w-full rounded-md bg-ramsalab-teal-blue px-4 py-2 text-sm font-medium text-white"
              >
                Add {pendingEntryIds.length} mapping(s)
              </button>
            )}
          </section>
        </div>
      </main>
    </>
  );
}
